import axios from 'axios';

// Set the absolute URL to the server
// In production, use the environment variable REACT_APP_API_URL
// or fallback to the Render URL for the backend
let baseURL;

if (process.env.NODE_ENV === 'production') {
  // For production, prioritize environment variable
  baseURL = process.env.REACT_APP_API_URL || 'https://pokematch-vslogger.onrender.com';
} else {
  // For development (align with running server default)
  baseURL = 'http://localhost:5100';
}

// Allow overriding the API URL via localStorage for testing (guarded)
if (process.env.NODE_ENV !== 'production') {
  const storedApiUrl = localStorage.getItem('apiBaseUrl');
  if (storedApiUrl && process.env.REACT_APP_ALLOW_API_OVERRIDE === 'true') {
    baseURL = storedApiUrl;
  }
}

// API baseURL is set above

// Create an instance of axios with base URL configuration
const api = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json'
  },
  // Set withCredentials to false to avoid CORS issues with cross-domain API calls
  withCredentials: false,
  // Add timeout to avoid hanging requests - shorter in production for better UX
  timeout: process.env.NODE_ENV === 'production' ? 15000 : 30000
});

// Request interceptor for debugging (disabled in production)
api.interceptors.request.use(
  config => {
    // Only log in development mode
    if (process.env.NODE_ENV === 'development') {
      console.log(`🔍 Making ${config.method.toUpperCase()} request to: ${config.baseURL}${config.url}`);
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Intercept requests and add the token
api.interceptors.request.use(
  config => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['x-auth-token'] = token;
    }
    
    // Add cache busting for GET requests to certain endpoints
    if (config.method === 'get' && 
        (config.url.includes('opponentdecks') || 
         config.url.includes('deck-categories'))) {
      // Add timestamp query parameter to force fresh data
      const timestamp = new Date().getTime();
      const separator = config.url.includes('?') ? '&' : '?';
      config.url = `${config.url}${separator}_t=${timestamp}`;
    }
    
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Intercept responses to handle errors globally
api.interceptors.response.use(
  response => response,
  error => {
    // Handle API errors
    console.error('API Error:', error.message);
    
    // Log detailed error information for debugging
    if (error.response) {
      console.error('Error status:', error.response.status);
      console.error('Error data:', error.response.data);
      
      if (error.response.status === 401) {
        // Check if this is specifically a token expiration
        if (error.response.data && error.response.data.expired) {
          console.warn('Authentication token has expired');
          // Clear token
          localStorage.removeItem('token');
          
          // Redirect to login page if not already there
          if (window.location.pathname !== '/login') {
            console.log('Redirecting to login page due to expired token');
            // Use a small delay to allow the console message to be seen
            setTimeout(() => {
              window.location.href = '/login?expired=true';
            }, 100);
          }
        } else if (error.response.data && error.response.data.msg && 
                   (error.response.data.msg.includes('No token') || 
                    error.response.data.msg.includes('Token is not valid'))) {
          // Only remove token if it's actually invalid
          localStorage.removeItem('token');
          console.warn('Authentication token removed due to invalid token');
          
          // Only redirect if not on a public page
          if (window.location.pathname !== '/login' && 
              window.location.pathname !== '/register' &&
              window.location.pathname !== '/') {
            window.location.href = '/login';
          }
        } else {
          // For other 401 errors (like admin access denied), don't log out
          console.warn('Access denied:', error.response.data.msg || 'Unauthorized');
        }
      } else if (error.response.status === 403) {
        // Forbidden - user is authenticated but lacks permissions
        console.warn('Forbidden:', error.response.data.msg || 'Access denied');
      }
    } else if (error.request) {
      // The request was made but no response was received
      console.error('No response received:', error.request);
    } else {
      // Something happened in setting up the request
      console.error('Request setup error:', error.message);
    }
    
    return Promise.reject(error);
  }
);

// API endpoints configuration
export const API_ENDPOINTS = {
  // Auth
  AUTH: {
    LOGIN: '/auth/login',
    REGISTER: '/auth/register',
    ME: '/auth/user',
    UPDATE_PROFILE: '/auth/profile',
  },
  
  // Decks
  DECKS: {
    LIST: '/decks',
    CREATE: '/decks',
    UPDATE: (id) => `/decks/${id}`,
    DELETE: (id) => `/decks/${id}`,
    CATEGORIES: '/deck-categories',
  },
  
  // Teams
  TEAMS: {
    LIST: '/teams',
    CREATE: '/teams',
    UPDATE: (id) => `/teams/${id}`,
    DELETE: (id) => `/teams/${id}`,
  },
  
  // Matches
  TCG_MATCHES: {
    LIST: '/tcgmatches',
    CREATE: '/tcgmatches',
    UPDATE: (id) => `/tcgmatches/${id}`,
    DELETE: (id) => `/tcgmatches/${id}`,
    STATS: '/tcgmatches/stats',
  },
  
  VGC_MATCHES: {
    LIST: '/vgcmatches',
    CREATE: '/vgcmatches',
    UPDATE: (id) => `/vgcmatches/${id}`,
    DELETE: (id) => `/vgcmatches/${id}`,
    STATS: '/vgcmatches/stats',
  },
  
  // Analytics
  ANALYTICS: {
    DECK: (id) => `/deck-analytics/${id}`,
    OVERALL: '/stats/overall',
    TRENDS: '/stats/trends',
  },
  
  // Friends
  FRIENDS: {
    LIST: '/friends',
    REQUESTS: '/friends/requests',
    SEND: '/friends/request',
    ACCEPT: (id) => `/friends/accept/${id}`,
    REJECT: (id) => `/friends/reject/${id}`,
  },
  
  // Notifications
  NOTIFICATIONS: {
    LIST: '/notifications',
    MARK_READ: (id) => `/notifications/${id}/read`,
    MARK_ALL_READ: '/notifications/read-all',
  },

  // Admin
  ADMIN: {
    MATCHES: '/admin/matches',
  },
};

// Helper functions for common API calls
export const apiHelpers = {
  // Generic GET with caching key
  get: (url, config = {}) => {
    return api.get(url, config);
  },
  
  // Generic POST
  post: (url, data, config = {}) => {
    return api.post(url, data, config);
  },
  
  // Generic PUT
  put: (url, data, config = {}) => {
    return api.put(url, data, config);
  },
  
  // Generic DELETE
  delete: (url, config = {}) => {
    return api.delete(url, config);
  },
  
  // Batch requests
  batch: (requests) => {
    return Promise.all(requests);
  },
};

export default api;
